/**
 * NOTE on the province prop: callers (EmailPasswordForm, etc.) pass the
 * selected province's *id* (from a province-id dropdown), but city lookups
 * are keyed by province *name* string. Resolved here by looking up the
 * province name from `locations.entities.provinces[id]` before dispatching
 * — keeps the calling screens simple and avoids changing their prop contract.
 *
 * FIX (was using coupon-common/src/modules/cities): that standalone module
 * is wired to a different, apparently-unmaintained reducer slice (state.cities)
 * and its own api.getCityList sends the province name in the `name` query
 * param instead of `province`, so it could never return matches. Switched to
 * coupon-common/src/modules/locations — the same duck CityPickerButtonModal
 * already uses successfully — which stores cities as normalized entities
 * under state.locations and exposes a debounced getFilteredCities(province, name)
 * action that matches the API's actual param contract.
 *
 * DESIGN: switched from a floating, position:absolute dropdown
 * (CityAutocomplete.module.scss) to the same pill-picker pattern used by
 * ServiceableAreaSection / BusinessCategoriesField's CategoryPillField
 * (auth-serviceable-box / auth-city-pill-row / auth-city-pill). This also
 * fixes a real scroll bug: the old dropdown was clipped by `.auth-page`'s
 * `overflow: hidden` once the list grew past the visible card, so no
 * amount of scrolling revealed the rest of the results. Rendering the
 * list inline (not as a floating overlay) avoids that clipping entirely;
 * a bounded, scrollable pill row (auth-city-pill-row-scroll, added to
 * AuthFlow.css) handles long lists instead.
 */
import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { actions as locationActions, duck as locationsDuck } from 'coupon-common/src/modules/locations';

const citiesKey = locationsDuck.citiesEntity.key;

/* ── inline search icon, matches ServiceableAreaSection's ── */
function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <circle cx="6.3" cy="6.3" r="5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const CityAutocomplete = ({ inputId, province, value, onChange, onBlur, error }) => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');

  const provinceRecord = useSelector(state => state.locations.entities.provinces?.[province]);
  const provinceName = provinceRecord?.name;

  const citiesResult = useSelector(state => state.locations.result?.[citiesKey]);
  const cityEntities = useSelector(state => state.locations.entities?.[citiesKey]) || {};
  const isFetching = !!citiesResult?.fetching;

  const cityOptions = useMemo(() => {
    const results = citiesResult?.results || [];
    return results.map(id => cityEntities[id]).filter(Boolean);
  }, [citiesResult, cityEntities]);

  useEffect(() => {
    if (provinceName) {
      dispatch(locationActions.getFilteredCities(provinceName, query));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceName, query, dispatch]);

  // Reflect the externally-controlled value (e.g. on edit/prefill) into the
  // visible query text once we have a city name to show for it.
  useEffect(() => {
    if (value && typeof value === 'object' && value.name) {
      setQuery(value.name);
    } else if (!value) {
      setQuery('');
    }
  }, [value]);

  // Server (getFilteredCities) already filters by `query`, but keep a light
  // client-side pass too in case results lag behind the latest keystroke.
  const filteredOptions = useMemo(() => {
    if (!query.trim()) return cityOptions;
    const lower = query.toLowerCase();
    return cityOptions.filter(city => city.name?.toLowerCase().includes(lower));
  }, [cityOptions, query]);

  const handleSelect = city => {
    onChange(city);
    setQuery(city.name);
  };

  return (
    <div className="auth-serviceable-box">
      <label className="auth-field auth-field-plain auth-serviceable-search">
        <input
          id={inputId}
          type="text"
          autoComplete="off"
          placeholder={provinceName ? 'Start typing your city' : 'Select a province first'}
          value={query}
          disabled={!provinceName}
          onChange={e => {
            setQuery(e.target.value);
            if (!e.target.value) onChange(null);
          }}
          onBlur={onBlur}
        />
        <SearchIcon />
      </label>

      {provinceName && (
        <div className="auth-city-pill-row auth-city-pill-row-scroll">
          {isFetching && <p className="text-muted">Loading cities…</p>}
          {!isFetching && filteredOptions.length === 0 && (
            <p className="text-muted">No matching cities.</p>
          )}
          {!isFetching &&
            filteredOptions.map(city => (
              <button
                key={city.id}
                type="button"
                className={`auth-city-pill${value?.id === city.id ? ' is-selected' : ''}`}
                onClick={() => handleSelect(city)}
              >
                {city.name}
              </button>
            ))}
        </div>
      )}

      {error && <span className="auth-field-error">{error}</span>}
    </div>
  );
};

export default CityAutocomplete;